import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Tooltip,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import { CaretLeft, CaretRight, MagnifyingGlass, Plus, X, ArrowsClockwise } from "@phosphor-icons/react";
import { http, DicomNode, Order, Tenant } from "../api";
import { useSession } from "../AuthToken";
import { useLive } from "../live";
import { Mono, PageHeader, StatusChip } from "../ui";

const STATUSES = [
  { value: "", label: "All" },
  { value: "scheduled", label: "Scheduled" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];
interface Page {
  items: Order[];
  nextCursor: string | null;
  total: number;
  byStatus: Record<string, number>;
  narrow?: boolean;
}

const pad = (n: number) => String(n).padStart(2, "0");
const isoDay = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const dayBounds = (day: string) => {
  const [y, m, d] = day.split("-").map(Number);
  return { from: new Date(y, m - 1, d), to: new Date(y, m - 1, d + 1) };
};
const shiftDay = (day: string, n: number) => {
  const { from } = dayBounds(day);
  return isoDay(new Date(from.getFullYear(), from.getMonth(), from.getDate() + n));
};

export default function Worklist() {
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const refreshFromEmr = async () => {
    if (!token) return;
    setSyncing(true);
    const r = await http.post<{ created: number; updated: number; skipped: number; seen: number }>(
      token,
      "/admin/emr/sync"
    );
    setSyncing(false);
    if (!r.ok) return setSyncMsg({ tone: "error", text: r.error });
    const c = r.result;
    setSyncMsg({
      tone: "success",
      text: `EMR refresh: ${c.seen} appointment${c.seen === 1 ? "" : "s"} seen, ${c.created} new, ${
        c.updated
      } updated.`,
    });
    load();
  };
  const { token, me } = useSession();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const today = isoDay(new Date());
  const day = /^\d{4}-\d{2}-\d{2}$/.test(params.get("date") ?? "") ? params.get("date")! : today;
  const status = params.get("status") ?? "";
  const station = params.get("station") ?? "";
  const [q, setQ] = useState(params.get("q") ?? "");
  const [query, setQuery] = useState(q.trim()); // debounced
  const [page, setPage] = useState<Page | null>(null);
  const [extra, setExtra] = useState<Order[]>([]);
  const [nodes, setNodes] = useState<DicomNode[]>([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };
  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(q.trim());
      setParam("q", q.trim());
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  useEffect(() => {
    if (token) http.get<DicomNode[]>(token, "/nodes").then((r) => r.ok && setNodes(r.result));
  }, [token]);

  const url = useCallback(
    (cursor?: string) => {
      const u = new URLSearchParams({ limit: "50" });
      if (query) u.set("q", query);
      else {
        const { from, to } = dayBounds(day);
        u.set("from", from.toISOString());
        u.set("to", to.toISOString());
      }
      if (status) u.set("status", status);
      if (station) u.set("station", station);
      if (cursor) u.set("cursor", cursor);
      return `/orders?${u}`;
    },
    [query, day, status, station]
  );
  const load = useCallback(async () => {
    if (!token) return;
    const r = await http.get<Page>(token, url());
    if (r.ok) {
      setPage(r.result);
      setExtra([]);
    } else setError(r.error);
  }, [token, url]);
  useEffect(() => {
    load();
  }, [load]);
  // keep the first page current from Jupiter's change stream (no polling); once more pages are loaded, stop
  // refreshing so rows do not move under the reader
  const extraRef = useRef(0);
  extraRef.current = extra.length;
  const pending = useRef<ReturnType<typeof setTimeout>>();
  useLive(token, () => {
    if (extraRef.current) return;
    clearTimeout(pending.current);
    pending.current = setTimeout(load, 300); // a sync writes many orders at once: a few re-fetches for the burst, not one per order
  });
  const more = async () => {
    const cursor = extra.length ? extra[extra.length - 1].id : page?.nextCursor;
    if (!cursor) return;
    const r = await http.get<Page>(token, url(cursor));
    if (r.ok) {
      setExtra((x) => [...x, ...r.result.items]);
      setPage((p) => (p ? { ...p, nextCursor: r.result.nextCursor } : p));
    } else setError(r.error);
  };
  const orders = [...(page?.items ?? []), ...extra];
  const exact = (o: Order) => !!query && (o.patient.mrn === query || o.accession.toUpperCase() === query.toUpperCase());
  const allCount = page ? Object.values(page.byStatus).reduce((a, b) => a + b, 0) : 0;

  return (
    <Stack spacing={2.5}>
      <PageHeader overline={me?.tenant?.name ?? "Orders"} title="Worklist">
        {(me?.role === "admin" || me?.role === "tech") && (
          <Tooltip title="Pull today's and the coming week's appointments from the practice's EMR now (they also arrive on their own as they are booked or changed)">
            <span>
              <Button
                variant="outlined"
                startIcon={<ArrowsClockwise size={16} />}
                disabled={syncing}
                onClick={refreshFromEmr}
              >
                {syncing ? "Refreshing..." : "Refresh from EMR"}
              </Button>
            </span>
          </Tooltip>
        )}
        {(me?.role === "admin" || me?.role === "tech") && (
          <Button variant="contained" startIcon={<Plus size={16} weight="regular" />} onClick={() => setOpen(true)}>
            New order
          </Button>
        )}
      </PageHeader>

      {syncMsg && (
        <Alert severity={syncMsg.tone} onClose={() => setSyncMsg(null)}>
          {syncMsg.text}
        </Alert>
      )}

      <Paper sx={{ p: 1.5 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }}>
          <TextField
            size="small"
            placeholder="MRN, accession, or name (family, given)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            sx={{ width: { xs: "100%", md: 320 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MagnifyingGlass size={16} />
                </InputAdornment>
              ),
              endAdornment: q ? (
                <InputAdornment position="end">
                  <IconButton size="small" aria-label="Clear search" onClick={() => setQ("")}>
                    <X size={14} />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ opacity: query ? 0.45 : 1 }}>
            <IconButton
              size="small"
              aria-label="Previous day"
              disabled={!!query}
              onClick={() => setParam("date", shiftDay(day, -1))}
            >
              <CaretLeft size={16} />
            </IconButton>
            <TextField
              size="small"
              type="date"
              value={day}
              disabled={!!query}
              onChange={(e) => e.target.value && setParam("date", e.target.value === today ? "" : e.target.value)}
              inputProps={{ "aria-label": "Day" }}
              sx={{ width: 165 }}
            />
            <IconButton
              size="small"
              aria-label="Next day"
              disabled={!!query}
              onClick={() => setParam("date", shiftDay(day, 1))}
            >
              <CaretRight size={16} />
            </IconButton>
            <Button size="small" disabled={!!query || day === today} onClick={() => setParam("date", "")}>
              Today
            </Button>
          </Stack>
          <TextField
            select
            size="small"
            label="Instrument"
            value={station}
            onChange={(e) => setParam("station", e.target.value)}
            sx={{ minWidth: 180 }}
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
          >
            <MenuItem value="">All instruments</MenuItem>
            {nodes.map((n) => (
              <MenuItem key={n.id} value={n.aet}>
                {n.displayName ? `${n.displayName} (${n.aet})` : n.aet}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }} alignItems="center">
          {STATUSES.map((st) => {
            const n = st.value ? page?.byStatus[st.value] ?? 0 : allCount;
            return (
              <Chip
                key={st.value}
                size="small"
                label={`${st.label} ${page ? n : ""}`.trim()}
                color={status === st.value ? "primary" : "default"}
                variant={status === st.value ? "filled" : "outlined"}
                onClick={() => setParam("status", st.value)}
              />
            );
          })}
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary">
            {query
              ? `Searching all dates for "${query}"`
              : day === today
              ? "Today"
              : dayBounds(day).from.toLocaleDateString([], {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
          </Typography>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {page?.narrow && (
        <Alert severity="info">
          More than 500 patients match this search, so not all of their orders are listed. Search by MRN or accession,
          or enter the name as family, given.
        </Alert>
      )}
      <Paper sx={{ overflowX: "auto" }}>
        <Table size="small" sx={{ minWidth: 860 }}>
          <TableHead>
            <TableRow>
              <TableCell>{query ? "Scheduled" : "Time"}</TableCell>
              <TableCell>Patient</TableCell>
              <TableCell>MRN</TableCell>
              <TableCell>Accession</TableCell>
              <TableCell>Instrument</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Captured objects</TableCell>
              <TableCell>Status</TableCell>
              <TableCell padding="checkbox" />
            </TableRow>
          </TableHead>
          <TableBody>
            {orders.map((o) => (
              <TableRow
                key={o.id}
                hover
                selected={exact(o)}
                sx={{ cursor: "pointer", "&:hover .row-go": { opacity: 1, transform: "none" } }}
                onClick={() => nav(`/orders/${o.id}`)}
              >
                <TableCell sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                  {query
                    ? new Date(o.scheduledAt).toLocaleString([], {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })
                    : new Date(o.scheduledAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 500 }}>
                  {o.patient.familyName}, {o.patient.givenName}
                </TableCell>
                <TableCell>
                  <Mono>{o.patient.mrn}</Mono>
                </TableCell>
                <TableCell>
                  <Mono sx={{ color: "primary.light" }}>{o.accession}</Mono>
                </TableCell>
                <TableCell>
                  <Mono>{o.stationAeTitle === "*" ? "All" : o.stationAeTitle}</Mono>
                </TableCell>
                <TableCell sx={{ color: "text.secondary" }}>{o.sourceSystem}</TableCell>
                <TableCell sx={{ color: "text.secondary" }}>
                  {o._count
                    ? [
                        `${o._count.instances} capture${o._count.instances === 1 ? "" : "s"}`,
                        o._count.sessions ? `${o._count.sessions} session${o._count.sessions === 1 ? "" : "s"}` : "",
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    : ""}
                </TableCell>
                <TableCell>
                  <StatusChip status={o.status} />
                </TableCell>
                <TableCell padding="checkbox">
                  <Box
                    className="row-go"
                    sx={{
                      display: "flex",
                      color: "primary.main",
                      opacity: 0,
                      transform: "translateX(-4px)",
                      transition: "all 0.2s",
                    }}
                  >
                    <CaretRight size={16} />
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {page && orders.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
                  {query ? "No orders match this search." : "No orders on this day."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
      {page && (
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
          <Typography variant="caption" color="text.secondary">
            Showing {orders.length} of {page.total}
          </Typography>
          {page.nextCursor && (
            <Button size="small" variant="outlined" onClick={more}>
              Load more
            </Button>
          )}
        </Stack>
      )}
      {open && (
        <NewOrderDialog
          onClose={(created) => {
            setOpen(false);
            if (created) load();
          }}
        />
      )}
    </Stack>
  );
}

function NewOrderDialog({ onClose }: { onClose: (created: boolean) => void }) {
  const { token } = useSession();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [nodes, setNodes] = useState<DicomNode[]>([]);
  const [f, setF] = useState({
    tenantCode: "",
    stationAeTitle: "",
    mrn: "",
    familyName: "",
    givenName: "",
    birthDate: "",
    sex: "",
    procedureDescription: "Remote slit lamp exam",
    modality: "OP",
  });
  const [error, setError] = useState("");
  useEffect(() => {
    http.get<DicomNode[]>(token, "/nodes").then((r) => r.ok && setNodes(r.result));
    http.get<Tenant[]>(token, "/admin/tenants").then((r) => {
      if (r.ok) {
        setTenants(r.result);
        if (r.result[0]) setF((x) => ({ ...x, tenantCode: x.tenantCode || r.result[0].code }));
      }
    });
  }, [token]);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    const r = await http.post(token, "/admin/orders", {
      tenantCode: f.tenantCode,
      stationAeTitle: f.stationAeTitle,
      modality: f.modality,
      procedureDescription: f.procedureDescription,
      patient: {
        mrn: f.mrn,
        familyName: f.familyName,
        givenName: f.givenName,
        ...(f.birthDate ? { birthDate: f.birthDate.replace(/-/g, "") } : {}),
        ...(f.sex ? { sex: f.sex } : {}),
      },
    });
    if (r.ok) onClose(true);
    else setError(r.error);
  };
  return (
    <Dialog open onClose={() => onClose(false)} fullWidth maxWidth="sm">
      <DialogTitle>New order</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {tenants.length > 1 && (
            <TextField select label="Tenant" value={f.tenantCode} onChange={set("tenantCode")}>
              {tenants.map((t) => (
                <MenuItem key={t.id} value={t.code}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            select
            label="Instrument"
            value={f.stationAeTitle}
            onChange={(e) => {
              const n = nodes.find((x) => x.aet === e.target.value);
              setF({
                ...f,
                stationAeTitle: e.target.value,
                modality: n?.modules.some((m) => m.moduleId.includes("cirrus")) ? "OPT" : "OP",
                procedureDescription: n?.modules.some((m) => m.moduleId.includes("cirrus"))
                  ? "OCT exam"
                  : "Remote slit lamp exam",
              });
            }}
            helperText="AE title of the instrument. The order appears on this instrument's worklist."
          >
            {nodes.map((n) => (
              <MenuItem key={n.id} value={n.aet}>
                {n.displayName ?? n.aet}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {n.aet} · {n.device.siteCode}
                </Typography>
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={2}>
            <TextField label="Family name" value={f.familyName} onChange={set("familyName")} fullWidth />
            <TextField label="Given name" value={f.givenName} onChange={set("givenName")} fullWidth />
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField label="MRN" value={f.mrn} onChange={set("mrn")} fullWidth />
            <TextField
              label="Birth date"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={f.birthDate}
              onChange={set("birthDate")}
              fullWidth
            />
            <TextField select label="Sex" value={f.sex} onChange={set("sex")} sx={{ minWidth: 100 }}>
              <MenuItem value="">Not specified</MenuItem>
              <MenuItem value="F">F</MenuItem>
              <MenuItem value="M">M</MenuItem>
              <MenuItem value="O">O</MenuItem>
            </TextField>
          </Stack>
          <TextField label="Procedure" value={f.procedureDescription} onChange={set("procedureDescription")} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button color="inherit" onClick={() => onClose(false)}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<Plus size={16} weight="regular" />}
          disabled={!f.stationAeTitle || !f.mrn || !f.familyName || !f.givenName}
          onClick={submit}
        >
          Create order
        </Button>
      </DialogActions>
    </Dialog>
  );
}
